/* ============================================
   NOVEL PAGE
   The Creative Orchestra - GEMINI Framework
   Novel reading interface with chapter navigation
   ============================================ */

import { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight, Clock, List } from 'lucide-react';
import { NovelReader, ResumeReadingCard, ReadingStatsCard } from '@components/novel';
import { ScrollReveal } from '@components/common';
import { chapters, chapterList, novelParts, getChapterById } from '@data';
import useBookmark from '@hooks/useBookmark';
import { SITE_INFO } from '@utils/constants';

/**
 * Novel Page Component
 * Handles both the novel landing (no chapter) and chapter reading
 */
const NovelPage = () => {
  const { chapterId } = useParams();
  const navigate = useNavigate();

  // Set page title
  useEffect(() => {
    if (chapterId) {
      const chapter = getChapterById(chapterId);
      document.title = chapter 
        ? `${chapter.title} | ${chapters.title}`
        : `${chapters.title} | ${SITE_INFO.title}`;
    } else {
      document.title = `${chapters.title} | ${SITE_INFO.title}`;
    }
  }, [chapterId]);

  // If chapter ID provided, show reader
  if (chapterId) {
    return <NovelReader />;
  }

  // Otherwise show novel landing page
  return <NovelLanding />;
};

/**
 * Novel Landing Page
 * Overview of the novel with chapter list
 */
const NovelLanding = () => {
  const { bookmark, hasReadingProgress, getReadingStats } = useBookmark();
  const stats = getReadingStats(chapterList.length);

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <ScrollReveal>
          <header className="text-center mb-12">
            <motion.div
              className="w-20 h-20 mx-auto mb-6 rounded-full 
                bg-gradient-to-br from-indigo-500/30 to-purple-500/20
                flex items-center justify-center border border-white/20"
              animate={{
                boxShadow: [
                  '0 0 30px rgba(129, 140, 248, 0.3)',
                  '0 0 50px rgba(129, 140, 248, 0.5)',
                  '0 0 30px rgba(129, 140, 248, 0.3)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <BookOpen className="w-8 h-8 text-white" />
            </motion.div>

            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              {chapters.title}
            </h1>
            
            <p className="text-xl text-gray-400 mb-2">
              {chapters.subtitle}
            </p>
            
            <p className="text-sm text-gray-500">
              By {chapters.author} • {chapters.totalChapters} Chapters • {chapters.estimatedReadingTime}
            </p>
          </header>
        </ScrollReveal>

        {/* Resume reading card */}
        {hasReadingProgress() && (
          <ScrollReveal delay={0.1}>
            <ResumeReadingCard className="mb-8" />
          </ScrollReveal>
        )}

        {/* Quick actions */}
        <ScrollReveal delay={0.2}>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link to="/novel/01">
              <motion.button
                className="flex items-center gap-2 px-6 py-3 rounded-full
                  bg-gradient-to-r from-indigo-500 to-purple-500
                  text-white font-medium"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(129, 140, 248, 0.5)' }}
                whileTap={{ scale: 0.95 }}
              >
                <BookOpen className="w-5 h-5" />
                {stats.chaptersRead > 0 ? 'Start from Beginning' : 'Begin Reading'}
              </motion.button>
            </Link>

            {stats.chaptersRead > 0 && stats.chaptersRead < stats.totalChapters && bookmark && (
              <Link to={`/novel/${bookmark.chapterId}`}>
                <motion.button
                  className="flex items-center gap-2 px-6 py-3 rounded-full
                    bg-white/10 border border-white/20 text-white font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ArrowRight className="w-5 h-5" />
                  Continue Reading
                </motion.button>
              </Link>
            )}
          </div>
        </ScrollReveal>

        {/* Reading stats (if started) */}
        {stats.chaptersRead > 0 && (
          <ScrollReveal delay={0.3}>
            <div className="mb-12">
              <ReadingStatsCard />
            </div>
          </ScrollReveal>
        )}

        {/* Table of Contents */}
        <ScrollReveal delay={0.4}>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <List className="w-5 h-5 text-indigo-400" />
              <h2 className="text-xl font-semibold text-white">Table of Contents</h2>
            </div>

            <div className="space-y-8">
              {novelParts.map((part) => (
                <PartSection key={part.number} part={part} />
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Tagline */}
        <ScrollReveal delay={0.5}>
          <p className="text-center text-gray-500 mt-12 italic">
            "{chapters.tagline}"
          </p>
        </ScrollReveal>
      </div>
    </div>
  );
};

/**
 * Part Section Component
 */
const PartSection = ({ part }) => {
  const { isChapterVisited } = useBookmark();
  
  const partChapters = useMemo(() => 
    part.chapterIds.map(id => chapterList.find(ch => ch.id === id)).filter(Boolean),
    [part.chapterIds]
  );

  const readCount = partChapters.filter(ch => isChapterVisited(ch.id)).length;

  return (
    <div>
      {/* Part header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {part.title}
          </h3>
          {part.subtitle && (
            <p className="text-sm text-gray-500">{part.subtitle}</p>
          )}
        </div>
        <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">
          {readCount}/{partChapters.length}
        </span>
      </div>

      {/* Chapters */}
      <div className="space-y-2">
        {partChapters.map((chapter) => (
          <ChapterRow 
            key={chapter.id} 
            chapter={chapter}
            isVisited={isChapterVisited(chapter.id)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Chapter Row Component
 */
const ChapterRow = ({ chapter, isVisited }) => {
  return (
    <Link to={`/novel/${chapter.id}`}>
      <motion.div
        className={`
          flex items-center justify-between p-3 rounded-lg
          transition-colors group
          ${isVisited 
            ? 'bg-green-500/5 hover:bg-green-500/10' 
            : 'bg-white/5 hover:bg-white/10'
          }
        `}
        whileHover={{ x: 4 }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Status indicator */}
          <div className={`
            w-2 h-2 rounded-full flex-shrink-0
            ${isVisited ? 'bg-green-500' : 'bg-gray-600'}
          `} />

          {/* Chapter info */}
          <div className="min-w-0">
            <p className="text-white group-hover:text-indigo-300 transition-colors truncate">
              {chapter.type === 'prologue' && 'Prologue: '}
              {chapter.type === 'epilogue' && 'Epilogue: '}
              {chapter.type === 'chapter' && `${chapter.number}. `}
              {chapter.title}
            </p>
            {chapter.subtitle && (
              <p className="text-xs text-gray-500 truncate">{chapter.subtitle}</p>
            )}
          </div>
        </div>

        {/* Reading time */}
        <div className="flex items-center gap-1 text-xs text-gray-500 flex-shrink-0 ml-4">
          <Clock className="w-3 h-3" />
          {chapter.readingTime}
        </div>
      </motion.div>
    </Link>
  );
};

export default NovelPage;
